// =========================================================
// ARMONIA MOBILE STICKY ADD TO CART — FINAL
// Keeps Dawn's native product form as the source of truth.
// =========================================================

if (!customElements.get('armonia-sticky-atc')) {
  customElements.define(
    'armonia-sticky-atc',
    class ArmoniaStickyAtc extends HTMLElement {
      connectedCallback() {
        // Move the fixed UI outside product layout/stacking contexts.
        // Moving a custom element reconnects it, so return and initialize
        // only after it is attached directly to <body>.
        if (this.parentElement !== document.body) {
          if (this.dataset.armoniaMoving === 'true') return;
          this.dataset.armoniaMoving = 'true';
          document.body.appendChild(this);
          return;
        }

        delete this.dataset.armoniaMoving;

        if (this.initialized) return;

        this.sectionId = this.dataset.sectionId || '';
        if (!this.sectionId) return;

        this.productInfo = document.getElementById(`MainProduct-${this.sectionId}`);
        if (!this.productInfo) return;

        this.stickyButton = this.querySelector('[data-armonia-sticky-button]');
        this.stickyLabel = this.querySelector('[data-armonia-sticky-label]');
        this.stickyPrice = this.querySelector('[data-armonia-sticky-price]');
        this.stickyCompare = this.querySelector('[data-armonia-sticky-compare]');

        if (!this.stickyButton || !this.stickyLabel || !this.stickyPrice || !this.stickyCompare) return;

        this.abortController = new AbortController();
        const { signal } = this.abortController;

        this.stickyButton.addEventListener(
          'click',
          () => {
            const mainButton = this.getMainButton();
            if (!mainButton || this.isMainButtonDisabled(mainButton)) return;

            mainButton.click();
            this.syncState();
          },
          { signal },
        );

        this.productInfo.addEventListener(
          'change',
          () => {
            requestAnimationFrame(() => this.syncState());
          },
          { signal },
        );

        this.resizeHandler = () => this.updateObserver();
        window.addEventListener('resize', this.resizeHandler, { passive: true, signal });

        this.mutationObserver = new MutationObserver(() => {
          if (this.syncFrame) cancelAnimationFrame(this.syncFrame);
          this.syncFrame = requestAnimationFrame(() => {
            this.syncState();
            this.updateObserver();
          });
        });

        this.mutationObserver.observe(this.productInfo, {
          subtree: true,
          childList: true,
          characterData: true,
          attributes: true,
          attributeFilter: ['disabled', 'aria-disabled', 'class'],
        });

        this.initialized = true;
        this.hidden = false;

        this.syncState();
        this.updateObserver();
      }

      disconnectedCallback() {
        if (this.dataset.armoniaMoving === 'true') return;

        this.abortController?.abort();
        this.mutationObserver?.disconnect();
        this.intersectionObserver?.disconnect();

        if (this.syncFrame) cancelAnimationFrame(this.syncFrame);

        document.body.classList.remove('armonia-sticky-atc-visible');

        this.initialized = false;
      }

      getMainButton() {
        return document.getElementById(`ProductSubmitButton-${this.sectionId}`);
      }

      getPriceRoot() {
        return document.getElementById(`price-${this.sectionId}`);
      }

      isMainButtonDisabled(button) {
        return (
          !button ||
          button.disabled ||
          button.getAttribute('aria-disabled') === 'true'
        );
      }

      getVisibleText(element) {
        if (!element) return '';
        return (element.textContent || '').replace(/\s+/g, ' ').trim();
      }

      readCurrentPrice() {
        const root = this.getPriceRoot();
        if (!root) return { current: '', compare: '' };

        const price = root.querySelector('.price');
        const isOnSale = price?.classList.contains('price--on-sale');

        let current = '';
        let compare = '';

        if (isOnSale) {
          current = this.getVisibleText(
            root.querySelector('.price__sale .price-item--sale'),
          );
          compare = this.getVisibleText(
            root.querySelector('.price__sale .price-item--regular'),
          );
        } else {
          current = this.getVisibleText(
            root.querySelector('.price__regular .price-item--regular'),
          );
        }

        if (!current) {
          current = this.getVisibleText(
            root.querySelector('.price-item:not(.price-item--regular)'),
          );
        }

        if (!current) {
          current = this.getVisibleText(root.querySelector('.price-item'));
        }

        return { current, compare };
      }

      syncState() {
        if (!this.initialized) return;

        const mainButton = this.getMainButton();
        if (!mainButton) {
          this.hideSticky();
          return;
        }

        const disabled = this.isMainButtonDisabled(mainButton);
        const loading = mainButton.classList.contains('loading');

        this.stickyButton.disabled = disabled || loading;
        this.stickyButton.classList.toggle('is-loading', loading);
        this.stickyButton.setAttribute('aria-busy', loading ? 'true' : 'false');

        const mainLabel =
          this.getVisibleText(mainButton.querySelector('span')) ||
          this.getVisibleText(mainButton);

        this.stickyLabel.textContent = disabled
          ? (mainLabel || 'Rasprodato')
          : 'Dodaj u korpu';

        const { current, compare } = this.readCurrentPrice();

        if (current) {
          this.stickyPrice.textContent = current;
        }

        if (compare && compare !== current) {
          this.stickyCompare.textContent = compare;
          this.stickyCompare.hidden = false;
        } else {
          this.stickyCompare.textContent = '';
          this.stickyCompare.hidden = true;
        }
      }

      updateObserver() {
        if (!this.initialized) return;

        const mainButton = this.getMainButton();
        if (!mainButton) {
          this.hideSticky();
          return;
        }

        if (this.observedMainButton === mainButton && this.intersectionObserver) {
          return;
        }

        this.intersectionObserver?.disconnect();

        this.observedMainButton = mainButton;

        this.intersectionObserver = new IntersectionObserver(
          (entries) => {
            const entry = entries[0];
            if (!entry) return;

            if (entry.isIntersecting) {
              this.hideSticky();
              return;
            }

            // Show only after the real Add to Cart has scrolled ABOVE
            // the viewport. Do not show prematurely while it is still below.
            if (entry.boundingClientRect.bottom <= 0) {
              this.showSticky();
            } else {
              this.hideSticky();
            }
          },
          {
            root: null,
            threshold: 0,
          },
        );

        this.intersectionObserver.observe(mainButton);
      }

      showSticky() {
        if (window.matchMedia('(min-width: 750px)').matches) {
          this.hideSticky();
          return;
        }

        this.classList.add('is-visible');
        document.body.classList.add('armonia-sticky-atc-visible');
      }

      hideSticky() {
        this.classList.remove('is-visible');
        document.body.classList.remove('armonia-sticky-atc-visible');
      }
    },
  );
}
